import { withoutGitBusyOverlay } from '../../utils/gitBusy';

/** Full-size photo viewer for item image tiles in FileView. */
export class ImageLightbox {
  private el: HTMLElement | null = null;
  private wiredContainer: HTMLElement | null = null;
  private loadId = 0;

  constructor(
    private readonly resolveImageUrl: (
      repoPath: string,
      fullSize?: boolean,
      bustCache?: boolean
    ) => Promise<string>
  ) {}

  wire(container: HTMLElement): void {
    if (this.wiredContainer === container) return;
    this.wiredContainer = container;
    container.addEventListener('click', e => {
      if ((e.target as HTMLElement).closest('.btn-remove-media')) return;
      const tile = (e.target as HTMLElement).closest('.item-image-tile');
      if (!tile) return;
      const img = tile.querySelector<HTMLImageElement>('img.item-image');
      if (!img || img.classList.contains('item-image-error')) return;
      e.preventDefault();
      void this.open(img);
    });
  }

  private ensureEl(): HTMLElement {
    if (this.el) return this.el;
    const lb = document.createElement('div');
    lb.id = 'image-lightbox';
    lb.className = 'image-lightbox hidden';
    lb.setAttribute('role', 'dialog');
    lb.setAttribute('aria-modal', 'true');
    lb.setAttribute('aria-label', 'Photo viewer');
    lb.innerHTML = `
      <button type="button" class="image-lightbox-close" aria-label="Close">×</button>
      <div class="image-lightbox-backdrop" aria-hidden="true"></div>
      <div class="image-lightbox-stage">
        <span class="image-lightbox-spinner app-spinner hidden" aria-hidden="true"></span>
        <p class="image-lightbox-error hidden">Could not load photo</p>
        <img class="image-lightbox-img hidden" alt="" />
      </div>`;
    const close = () => this.close();
    lb.querySelector('.image-lightbox-close')!.addEventListener('click', close);
    lb.querySelector('.image-lightbox-backdrop')!.addEventListener('click', close);
    document.addEventListener('keydown', e => {
      if (e.key === 'Escape' && !lb.classList.contains('hidden')) close();
    });
    document.body.appendChild(lb);
    this.el = lb;
    return lb;
  }

  private setLoading(lb: HTMLElement, loading: boolean): void {
    const spinner = lb.querySelector<HTMLElement>('.image-lightbox-spinner');
    const err = lb.querySelector<HTMLElement>('.image-lightbox-error');
    const big = lb.querySelector<HTMLImageElement>('.image-lightbox-img');
    lb.classList.toggle('image-lightbox--loading', loading);
    spinner?.classList.toggle('hidden', !loading);
    err?.classList.add('hidden');
    if (big) {
      big.classList.toggle('hidden', loading);
      if (loading) {
        big.removeAttribute('src');
        big.onload = null;
        big.onerror = null;
      }
    }
  }

  private showError(lb: HTMLElement): void {
    lb.classList.remove('image-lightbox--loading');
    lb.querySelector('.image-lightbox-spinner')?.classList.add('hidden');
    lb.querySelector('.image-lightbox-error')?.classList.remove('hidden');
    const big = lb.querySelector<HTMLImageElement>('.image-lightbox-img');
    big?.classList.add('hidden');
    if (big) {
      big.removeAttribute('src');
      big.onload = null;
      big.onerror = null;
    }
  }

  private async reveal(lb: HTMLElement, big: HTMLImageElement, url: string, loadId: number): Promise<void> {
    if (loadId !== this.loadId || lb.classList.contains('hidden')) return;
    await new Promise<void>((resolve, reject) => {
      const finish = () => {
        if (loadId !== this.loadId) {
          reject(new Error('cancelled'));
          return;
        }
        resolve();
      };
      big.onload = finish;
      big.onerror = () => reject(new Error('Image display failed'));
      big.removeAttribute('src');
      big.src = url;
      if (big.complete && big.naturalWidth > 0) finish();
    });
    if (loadId !== this.loadId || lb.classList.contains('hidden')) return;
    big.onerror = null;
    big.onload = null;
    this.setLoading(lb, false);
    big.classList.remove('hidden');
  }

  async open(img: HTMLImageElement): Promise<void> {
    const repoPath = img.getAttribute('data-repo-path') ?? img.dataset['repoPath'];
    const loadId = ++this.loadId;
    const lb = this.ensureEl();
    const big = lb.querySelector<HTMLImageElement>('.image-lightbox-img')!;
    lb.classList.remove('hidden');
    document.body.style.overflow = 'hidden';
    big.alt = img.alt || 'Photo';
    this.setLoading(lb, true);
    const active = () => loadId === this.loadId && !lb.classList.contains('hidden');
    try {
      if (repoPath) {
        try {
          const url = await withoutGitBusyOverlay(() => this.resolveImageUrl(repoPath, true));
          if (!active()) return;
          await this.reveal(lb, big, url, loadId);
        } catch {
          if (!active()) return;
          const url = await withoutGitBusyOverlay(() => this.resolveImageUrl(repoPath, true, true));
          if (!active()) return;
          await this.reveal(lb, big, url, loadId);
        }
      } else {
        const localSrc = img.currentSrc || img.src;
        if (!localSrc || localSrc === window.location.href) {
          this.close();
          return;
        }
        if (!active()) return;
        await this.reveal(lb, big, localSrc, loadId);
      }
    } catch {
      if (!active()) return;
      this.showError(lb);
    }
    if (!active()) return;
    lb.querySelector<HTMLButtonElement>('.image-lightbox-close')?.focus();
  }

  close(): void {
    this.loadId++;
    if (!this.el) return;
    this.el.classList.add('hidden');
    this.el.classList.remove('image-lightbox--loading');
    const big = this.el.querySelector<HTMLImageElement>('.image-lightbox-img');
    this.el.querySelector('.image-lightbox-spinner')?.classList.add('hidden');
    this.el.querySelector('.image-lightbox-error')?.classList.add('hidden');
    if (big) {
      big.onload = null;
      big.onerror = null;
      big.removeAttribute('src');
      big.alt = '';
      big.classList.add('hidden');
    }
    document.body.style.overflow = '';
  }
}
